import type { Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';

import type { BookingMomentSection, BookingMomentView } from './bookingMoment';

/**
 * El lado mínimo de un blanco táctil, en píxeles. Es el mismo número que ya
 * usa la fila de cierre de las cards de resultados: el pulgar es el mismo en
 * las dos pantallas.
 */
export const MOBILE_TAP_TARGET = 44;

/**
 * Qué secciones GOBIERNAN la decisión: las que pueden bloquear el CTA o mover
 * el número que se está por aceptar.
 *
 * Es un `Record` exhaustivo a propósito: sumar una sección al flujo no compila
 * hasta contestar esta pregunta, y de acá sale lo que en mobile NUNCA se puede
 * plegar. Si fuera una lista aparte de la que decide el plegado, una sección
 * nueva podría nacer escondiendo lo que frena el botón.
 */
export const BOOKING_SECTION_GATES_DECISION: Record<BookingMomentSection, boolean> = {
  // El proveedor movió la plata: es LO que se está decidiendo.
  reprice: true,
  trip: false,
  // Cambiar un horario obliga a re-cotizar, y eso bloquea el hold.
  schedule: true,
  // Plazas y asientos: el operador puede rechazar la selección.
  offerOptions: true,
  // Los datos que la oferta declara obligatorios.
  passengers: true,
  // Adicionales elegidos que se reservan EN el hold (#2383): mueven el total
  // que devuelve el hub y el operador puede rechazar la selección.
  optionalItems: true,
  conditions: false,
  // El rechazo del operador y las tarjetas de recuperación viven acá.
  status: true,
  reserved: false,
  // Obligatorias sin responder ⇒ el confirm falla.
  questions: true,
  // Lo que falta para poder confirmar.
  confirmPending: true,
  documents: false,
  extras: false,
  record: false,
};

/**
 * Las secciones de REPASO: lo que el asesor ya leyó antes de reservar y que
 * después vuelve a tener delante como referencia.
 */
const RECAP_SECTIONS: BookingMomentSection[] = ['trip', 'conditions'];

/**
 * Qué arranca plegado en mobile.
 *
 * Solo con la reserva ya creada, y solo el repaso: en Revisar todo está
 * abierto porque todo se está decidiendo por primera vez. Los dos filtros son
 * la invariante, no defensa: nada que frene el CTA puede quedar escondido
 * detrás de un toque, y nada que el momento no dibuje puede figurar acá.
 *
 * Recibe la VISTA entera —no el momento— para que la respuesta sea, por
 * construcción, un subconjunto de lo que la pantalla realmente dibuja: si
 * fueran dos listas parecidas, esta podría nombrar una sección ausente (una
 * regla que no gobierna nada) o quedarse corta cuando el momento suma una.
 *
 * En desktop no se pliega nada — la pantalla entra entera y esconder es
 * trabajo de más.
 */
export function collapsedBookingSectionsOnMobile(
  view: BookingMomentView,
): BookingMomentSection[] {
  if (view.moment === 'review') return [];
  return view.sections.filter(
    (section) =>
      RECAP_SECTIONS.includes(section) && !BOOKING_SECTION_GATES_DECISION[section],
  );
}

/**
 * Los blancos táctiles del flujo, en UNA definición y SOLO bajo el breakpoint
 * de mobile: botones, íconos, chips clickeables y asientos crecen al mínimo
 * que un pulgar acierta. En desktop no emite una sola regla — esa pantalla ya
 * quedó cerrada.
 *
 * Va en el contenedor y no en cada control porque el modal los dibuja desde
 * media docena de componentes: repetir el mínimo en cada uno es exactamente
 * cómo uno queda afuera.
 */
export function mobileTapTargetSx(theme: Theme): SystemStyleObject<Theme> {
  return {
    [theme.breakpoints.down('sm')]: {
      '& .MuiButton-root, & .MuiIconButton-root, & .MuiToggleButton-root': {
        minHeight: MOBILE_TAP_TARGET,
        minWidth: MOBILE_TAP_TARGET,
      },
      // Solo los que se tocan: un chip de estado no es un blanco táctil y
      // agrandarlo solo gasta pantalla. La altura la fija MUI por tamaño, así
      // que el mínimo se pide con `auto` + `minHeight`.
      '& .MuiChip-clickable': { height: 'auto', minHeight: MOBILE_TAP_TARGET },
    },
  };
}
